import { useState } from 'react';

import { t } from '@web/i18n';
import { Button, Dialog, DialogContent, DialogTitle, Textarea } from '@web/ui/core/atoms';

/** Bounds the kanban-api enforces on the (trimmed) justification. */
const MIN_LENGTH = 10;
const MAX_LENGTH = 1000;

/**
 * Asks why the operator is viewing as the traveler before the grant is
 * opened. Mirrors `CloseConfirmDialog`; the text starts from `initial` (the
 * card suggestion or the previous justification) and stays editable.
 */
export function JustificationDialog({
  travelerName,
  initial,
  pending,
  error,
  onConfirm,
  onCancel,
}: Readonly<{
  travelerName: string;
  initial: string;
  pending: boolean;
  /** Copy shown under the textarea when the last attempt failed. */
  error: string | null;
  onConfirm: (justification: string) => void;
  onCancel: () => void;
}>): React.JSX.Element {
  const [text, setText] = useState(initial);
  const justification = text.trim();
  const valid = justification.length >= MIN_LENGTH;
  return (
    <Dialog
      open
      onOpenChange={(o) => {
        if (!o) onCancel();
      }}
    >
      <DialogContent>
        <DialogTitle className="text-base font-medium text-foreground">
          {t('viewAs.dialog.title', { name: travelerName })}
        </DialogTitle>
        <p className="text-meta text-muted-foreground">{t('viewAs.dialog.body')}</p>
        <Textarea
          aria-label={t('viewAs.dialog.reasonLabel')}
          placeholder={t('viewAs.dialog.reasonPlaceholder')}
          value={text}
          maxLength={MAX_LENGTH}
          onChange={(e) => {
            setText(e.target.value);
          }}
        />
        {error ? (
          <p role="alert" className="text-meta text-destructive">
            {error}
          </p>
        ) : null}
        <div className="flex justify-end gap-2">
          <Button variant="ghost" size="sm" onClick={onCancel}>
            {t('viewAs.dialog.cancel')}
          </Button>
          <Button
            variant="primary"
            size="sm"
            disabled={!valid || pending}
            onClick={() => {
              onConfirm(justification);
            }}
          >
            {t('viewAs.dialog.confirm')}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
